import { createContext, type ReactNode, useContext } from 'react';

import { getAppVersion } from '@/utils/appVersion/getAppVersion';

const AppVersionContext = createContext<string | undefined>(undefined);

export type AppVersionProviderProps = {
    version: string;
    children: ReactNode;
};

/**
 * Provides the resolved global `appVersion` to client-side consumers.
 *
 * Appshell wraps the app during SSR with the version returned by
 * `resolveGlobalVersion()`; MFE client code reads it via `useAppVersion()`.
 */
export function AppVersionProvider({ version, children }: AppVersionProviderProps) {
    return <AppVersionContext.Provider value={version}>{children}</AppVersionContext.Provider>;
}

/**
 * Returns the resolved global `appVersion` from `AppVersionProvider` context.
 *
 * Falls back to `getAppVersion()` when used outside a provider (e.g. an MFE
 * rendered directly in local dev / platypus), so consumers always receive a
 * valid version string.
 */
export function useAppVersion(): string {
    const contextVersion = useContext(AppVersionContext);
    return contextVersion ?? getAppVersion();
}
